// Read-side helpers for presenting an account's subscription + credit state.
//
// MIRROR — `splitCreditBuckets` matches split_buckets() in
// packages/functions/users/credits/allotment.py. Keep the two in sync: the
// legacy fallback in particular is load-bearing, because an account that
// predates the two-bucket split holds only `balance`, and showing that as
// expiring monthly credits would tell someone their purchased packs are about
// to vanish.

import { PRICING_TIERS } from './plans';

// MIRROR — SUBSCRIBER_STATUSES matches billing/api/main.py, where it guards
// create_subscription_checkout against double-charging. Predicating the UI on
// the effective tier instead lets the two disagree: a past_due account past
// its 60-day grace reads as tier `free` on the client while the server still
// sees an existing subscription, so the page would offer a checkout the
// callable then rejects with FAILED_PRECONDITION.
export const SUBSCRIBER_STATUSES = ['active', 'past_due'];

export function isSubscriber(subscription) {
  return Boolean(subscription) && SUBSCRIBER_STATUSES.includes(subscription.status);
}

// Whether Stripe holds a customer record for this account — true for lapsed
// and cancelled subscriptions too, which is the point: those accounts read as
// tier `free` but are exactly the ones who need the portal to fix a declined
// card, pull an invoice, or reactivate.
export function hasBillingAccount(subscription) {
  return Boolean(subscription && subscription.stripeCustomerId);
}

export function getTierById(tierId) {
  return PRICING_TIERS.find((tier) => tier.id === tierId) || null;
}

// Position of a tier in the catalog, which is ordered cheapest-first. Used to
// tell an upgrade from a downgrade (#350) without hardcoding a tier ladder.
// Unknown tiers (e.g. a Business account outside the self-serve catalog) return
// null, and callers must then classify the move as neither — reporting a
// Business account as a "downgrade from Pro" would be worse than reporting
// nothing at all.
export function tierRank(tierId) {
  const index = PRICING_TIERS.findIndex((tier) => tier.id === tierId);
  return index === -1 ? null : index;
}

// 'tier_upgraded' | 'tier_downgraded' | null for a tier move.
export function tierChangeDirection(fromTierId, toTierId) {
  if (!fromTierId || !toTierId || fromTierId === toTierId) return null;
  const from = tierRank(fromTierId);
  const to = tierRank(toTierId);
  if (from == null || to == null) return null;
  if (to > from) return 'tier_upgraded';
  if (to < from) return 'tier_downgraded';
  return null;
}

export function splitCreditBuckets(credits) {
  const data = credits || {};
  const allotment = data.allotmentBalance;
  const topUp = data.topUpBalance;
  if (allotment == null && topUp == null) {
    // Everything a legacy account holds was purchased or granted at signup,
    // and neither expires — so it is all top-up.
    return { allotment: 0, topUp: Number(data.balance) || 0 };
  }
  return { allotment: Number(allotment) || 0, topUp: Number(topUp) || 0 };
}

// Stripe period bounds are epoch SECONDS (matching the server-side
// entitlements comparison), not milliseconds.
export function formatPeriodDate(epochSeconds) {
  if (!Number.isFinite(epochSeconds) || epochSeconds <= 0) return null;
  return new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
